import { useEffect, useMemo, useState } from "react";
import CryptoModal from "../CryptoModal";
import GuideHint from "../GetStartedGuide/GuideHint";
import { BUY_GUIDE_HINTS } from "../GetStartedGuide/buyGuideConfig";
import BuySellHeaderTabs from "./BuySellHeaderTabs";
import SelectFiatAccountStep from "./SelectFiatAccountStep";
import SelectCryptoAccountStep from "./SelectCryptoAccountStep";
import SelectAssetStep from "./SelectAssetStep";
import EnterAmountStep from "./EnterAmountStep";
import ConfirmOrderStep from "./ConfirmOrderStep";
import ProcessingStep from "./ProcessingStep";
import OrderSuccessStep from "./OrderSuccessStep";
import { ChevronRightIcon } from "../../icons";
import ModalStepper from "../common/ModalStepper";

const BUY_STEPS = [
  { id: 1, label: "Choose Account" },
  { id: 2, label: "Choose Asset" },
  { id: 3, label: "Enter Amount" },
  { id: 4, label: "Confirm Order" },
];

const SELL_STEPS = [
  { id: 1, label: "Choose Asset" },
  { id: 2, label: "Choose Account" },
  { id: 3, label: "Enter Amount" },
  { id: 4, label: "Confirm Order" },
];

const BuySellModal = ({
  isOpen,
  onClose,
  showBuyGuide = false,
  isNewUser = false,
  totalBalanceUsd,
  onGuideDepositCrypto,
  onGuideComplete,
  setLoadData
}) => {
  const [activeTab, setActiveTab] = useState("Buy");
  const [currentStep, setCurrentStep] = useState(1);
  const [modalPhase, setModalPhase] = useState("form");
  const [selectedFiatAccount, setSelectedFiatAccount] = useState(null);
  const [selectedCryptoAccount, setSelectedCryptoAccount] = useState(null);
  const [amount, setAmount] = useState("");
  const [rfq, setRfq] = useState(null);
  const [guideSkipped, setGuideSkipped] = useState(false);
  const [inputMode, setInputMode] = useState("fiat");
  const [error, setError] = useState("");


  const isSell = activeTab === "Sell";
  const steps = isSell ? SELL_STEPS : BUY_STEPS;

  const resetFormState = () => {
    setCurrentStep(1);
    setModalPhase("form");
    setSelectedFiatAccount(null);
    setSelectedCryptoAccount(null);
    // setSelectedCryptoAccount(null);
    setAmount("");
  };

  const resetAllState = () => {
    setActiveTab("Buy");
    resetFormState();
    setGuideSkipped(false);
  };

  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    if (modalPhase !== "form") return;

    if (currentStep > 1) {
      if (currentStep === 2) {
        if (isSell) {
          setSelectedFiatAccount(null);
        } else {
          setSelectedCryptoAccount(null);
        }
        setAmount("");
      }

      if (currentStep === 3) {
        setAmount("");
      }

      setCurrentStep((step) => step - 1);
      return;
    }

    handleClose();
  };

  const handleTabChange = (tab) => {
    setActiveTab((tab));
    if (tab !== activeTab) resetFormState();
  }

  const handleBuyAccountSelect = (account) => {
    setSelectedFiatAccount(account);
    setError(null);
    setCurrentStep(2);
  };

  const handleBuyAssetSelect = (asset) => {
    setSelectedCryptoAccount(asset);
    setCurrentStep(3);
  };

  const handleSellCryptoAccountSelect = (account) => {
    setSelectedCryptoAccount(account);
    setCurrentStep(2);
  };

  const handleSellDestinationSelect = (account) => {
    setSelectedFiatAccount(account);
    setError(null);
    setCurrentStep(3);
  };


  useEffect(() => {
    if (isOpen) {
      resetAllState();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  const showStepper = modalPhase === "form";
  const stepperStep = modalPhase === "form" ? currentStep : 4;

  const renderFormStep = () => {
    if (isSell) {
      if (currentStep === 1) {
        return (
          <SelectCryptoAccountStep
            onAccountSelect={handleSellCryptoAccountSelect}
          />
        );
      }
      if (currentStep === 2) {
        return (
          <SelectFiatAccountStep
            title="Select Destination Account"
            sectionLabel="Your Fiat Accounts"
            onAccountSelect={handleSellDestinationSelect}
          />
        );
      }
      if (currentStep === 3) {
        return (
          <EnterAmountStep
            activeTab={activeTab}
            selectedFiatAccount={selectedFiatAccount}
            selectedCryptoAccount={selectedCryptoAccount}
            amount={amount}
            onAmountChange={setAmount}
            onCryptoAccountClick={() => setCurrentStep(1)}
            onDestinationClick={() => setCurrentStep(2)}
            inputMode={inputMode}
            error={error}
            setError={setError}
            setInputMode={setInputMode}
            onContinue={() => {
              setCurrentStep(4)
              setError(null);
            }
            }
          />
        );
      }
      if (currentStep === 4) {
        return (
          <ConfirmOrderStep
            activeTab={activeTab}
            selectedFiatAccount={selectedFiatAccount}
            selectedCryptoAccount={selectedCryptoAccount}
            amount={amount}
            modalPhase={modalPhase} 
            setModalPhase={setModalPhase}
            setRfq={setRfq}
            rfq={rfq}
            inputMode={inputMode}
            error={error}
            setError={setError}
            handleBack={handleBack}
            spotlightActive={Boolean(confirmOrderGuideHint)}
            guideHint={confirmOrderGuideHint}
          />
        );
      }
      return null;
    }

    if (currentStep === 1) {
      return (
        <SelectFiatAccountStep
          title="Select Fiat Account"
          sectionLabel="Your Fiat Accounts"
          placeholder="Search Account..."
          onAccountSelect={handleBuyAccountSelect}
          spotlightActive={Boolean(selectAccountGuideHint)}
          guideHint={selectAccountGuideHint}
        />
      );
    }
    if (currentStep === 2) {
      return (
        <SelectAssetStep
          onAssetSelect={handleBuyAssetSelect}
          spotlightActive={Boolean(selectAssetGuideHint)}
          guideHint={selectAssetGuideHint}
        />
      );
    }
    if (currentStep === 3) {
      return (
        <EnterAmountStep
          activeTab={activeTab}
          selectedFiatAccount={selectedFiatAccount}
          selectedCryptoAccount={selectedCryptoAccount}
          amount={amount}
          onAmountChange={setAmount}
          onAccountClick={() => setCurrentStep(1)}
          onAssetClick={() => setCurrentStep(2)}
          inputMode={inputMode}
          setInputMode={setInputMode}
          onContinue={() => {
              setCurrentStep(4)
              setError(null);
            }
          }
          error={error}
          setError={setError}

          spotlightActive={Boolean(enterAmountGuideHint)}
          guideHint={enterAmountGuideHint}
        />
      );
    }
    if (currentStep === 4) {
      return (
        <ConfirmOrderStep
          activeTab={activeTab}
          selectedFiatAccount={selectedFiatAccount}
          selectedCryptoAccount={selectedCryptoAccount}
          amount={amount}
          modalPhase={modalPhase} 
          setModalPhase={setModalPhase}
          setRfq={setRfq}
          rfq={rfq}
          inputMode={inputMode}
          error={error}
          setError={setError}
          handleBack={handleBack}

          spotlightActive={Boolean(confirmOrderGuideHint)}
          guideHint={confirmOrderGuideHint}
        />
      );
    }
    return null;
  };

  const activeGuideHint = useMemo(() => {
    if (!isOpen || !showBuyGuide || guideSkipped || activeTab !== "Buy") {
      return null;
    }

    let hint = null;
    if (modalPhase === "form") {
      if (currentStep === 1) hint = BUY_GUIDE_HINTS.selectAccount;
      else if (currentStep === 2) hint = BUY_GUIDE_HINTS.selectAsset;
      else if (currentStep === 3) hint = BUY_GUIDE_HINTS.enterAmount;
      else if (currentStep === 4) hint = BUY_GUIDE_HINTS.confirmOrder;
    } else if (modalPhase === "success") {
      hint = BUY_GUIDE_HINTS.purchaseComplete;
    }

    return hint;
  }, [isOpen, showBuyGuide, guideSkipped, activeTab, modalPhase, currentStep]);

  const hideGuideHints = () => {
    setGuideSkipped(true);
  };

  const completeGuide = () => {
    hideGuideHints();
    onGuideComplete?.();
  };

  const guideHintTitle = activeGuideHint
    ? activeGuideHint.useStepPrefix === false
      ? activeGuideHint.title
      : `Step ${activeGuideHint.step}: ${activeGuideHint.title}`
    : "";

  const selectAccountGuideHint =
    activeGuideHint?.id === "select-account"
      ? {
          title: `Step ${activeGuideHint.step}: ${activeGuideHint.title}`,
          description: activeGuideHint.description,
          primaryAction: activeGuideHint.primaryAction,
          secondaryAction: activeGuideHint.secondaryAction,
          onSkip: hideGuideHints,
          onDone: completeGuide,
          onDepositCrypto: () => {
            hideGuideHints();
            onGuideDepositCrypto?.();
          },
        }
      : null;

  const selectAssetGuideHint =
    activeGuideHint?.id === "select-asset"
      ? {
          title: `Step ${activeGuideHint.step}: ${activeGuideHint.title}`,
          description: activeGuideHint.description,
          primaryAction: activeGuideHint.primaryAction,
          secondaryAction: activeGuideHint.secondaryAction,
          onSkip: hideGuideHints,
          onDone: completeGuide,
          onDepositCrypto: () => {
            hideGuideHints();
            onGuideDepositCrypto?.();
          },
        }
      : null;

  const enterAmountGuideHint =
    activeGuideHint?.id === "enter-amount"
      ? {
          title: `Step ${activeGuideHint.step}: ${activeGuideHint.title}`,
          description: activeGuideHint.description,
          primaryAction: activeGuideHint.primaryAction,
          secondaryAction: activeGuideHint.secondaryAction,
          onSkip: hideGuideHints,
          onDone: completeGuide,
          onDepositCrypto: () => {
            hideGuideHints();
            onGuideDepositCrypto?.();
          },
        }
      : null;

  const confirmOrderGuideHint =
    activeGuideHint?.id === "confirm-order"
      ? {
          title: `Step ${activeGuideHint.step}: ${activeGuideHint.title}`,
          description: activeGuideHint.description,
          primaryAction: activeGuideHint.primaryAction,
          secondaryAction: activeGuideHint.secondaryAction,
          onSkip: hideGuideHints,
          onDone: completeGuide,
          onDepositCrypto: () => {
            hideGuideHints();
            onGuideDepositCrypto?.();
          },
        }
      : null;

  const purchaseCompleteGuideHint =
    activeGuideHint?.id === "purchase-complete"
      ? {
          title: activeGuideHint.title,
          description: activeGuideHint.description,
          primaryAction: activeGuideHint.primaryAction,
          secondaryAction: activeGuideHint.secondaryAction,
          onSkip: hideGuideHints,
          onDone: completeGuide,
          onDepositCrypto: () => {
            hideGuideHints();
            onGuideDepositCrypto?.();
          },
        }
      : null;

  const inlineMobileGuideHint = selectAccountGuideHint || selectAssetGuideHint || enterAmountGuideHint || confirmOrderGuideHint;
  const fixedBottomMobileGuideHint = purchaseCompleteGuideHint;
  const mobileGuideDimActive = Boolean(inlineMobileGuideHint || purchaseCompleteGuideHint);
  const isBuyEnterAmountStep = modalPhase === "form" && !isSell && currentStep === 3;

  const guideHint = activeGuideHint
    ? {
        position: activeGuideHint.position,
        inlineOnMobile: activeGuideHint.inlineOnMobile,
        content: (
          <GuideHint
            title={guideHintTitle}
            description={activeGuideHint.description}
            position={activeGuideHint.position}
            showPointer={activeGuideHint.showPointer}
            primaryAction={activeGuideHint.primaryAction}
            secondaryAction={activeGuideHint.secondaryAction}
            onSkip={hideGuideHints}
            onDone={completeGuide}
            onDepositCrypto={() => {
              hideGuideHints();
              onGuideDepositCrypto?.();
            }}
          />
        ),
      }
    : null;
    
  return (
    <CryptoModal
      isOpen={isOpen}
      onClose={handleClose}
      mobileFullScreen
      mobileSpotlightActive={mobileGuideDimActive}
      wrapperClassName={"w-full max-w-none sm:max-w-[650px]"}
      guideHint={guideHint}
      closeOnOutsideClick={false}
      panelClassName={`flex flex-col sm:px-4 ${
        mobileGuideDimActive ? "max-sm:overflow-visible" : "overflow-hidden"
      }`}
      showCancel={(modalPhase !== "processing" || modalPhase !== "success" )}
      showBack={(modalPhase !== "processing" && modalPhase !== "success" )}
      headerLeft={
        <button
          type="button"
          onClick={handleBack}
          className="rounded-full p-0 custom-transition hover:bg-option-hover max-md:hidden"
          aria-label="Go back"
        >
          <ChevronRightIcon className="rotate-180 text-text-primary" />
        </button>
      }
      headerTabs={
        modalPhase === "form" ? (
          <BuySellHeaderTabs
            active={activeTab}
            onChange={handleTabChange}
            onBack={handleBack}
            isNewUser={isNewUser}
            totalBalanceUsd={totalBalanceUsd}
          />
        ) : null
      }
    >
      <div
        className={`flex min-h-0 flex-1 flex-col max-sm:pt-2 ${
          mobileGuideDimActive
            ? "max-sm:overflow-visible"
            : "overflow-hidden"
        }`}
      >
        {showStepper && (
          <>
            <ModalStepper
              steps={steps}
              currentStep={stepperStep}
            />
            <div className="mx-4 mt-3 shrink-0 border-t border-divider max-sm:hidden  sm:mx-6 sm:mt-3" />
          </>
        )}
        <div
          className={`flex min-h-0 w-full flex-1 flex-col font-outfit ${
            modalPhase === "success"
              ? "sm:min-h-0 sm:flex-1"
              : inlineMobileGuideHint && !isBuyEnterAmountStep
                ? "shrink-0 sm:min-h-0 sm:flex-1"
                : "min-h-0 flex-1"
          } ${mobileGuideDimActive ? "max-sm:overflow-visible" : "overflow-y-scroll hide-scrollbar"}`}
        >
          {modalPhase === "form" && renderFormStep()}
          {modalPhase === "processing" && <ProcessingStep />}
          {modalPhase === "success" && (
            <OrderSuccessStep
              activeTab={activeTab}
              selectedFiatAccount={selectedFiatAccount}
              selectedCryptoAccount={selectedCryptoAccount}
              amount={amount}
              rfq={rfq}
              onClose={() => {
                setLoadData((prev) => !prev);
                onClose();
              }}
              // fiatAmount={amount}
              // cryptoAmount={amount}
              // onCheckAssets={handleClose}
            />
          )}
        </div>
      </div>

      {fixedBottomMobileGuideHint && (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex justify-center px-4 sm:hidden">
          <div className="pointer-events-auto w-full max-w-[300px]">
            <GuideHint
              title={fixedBottomMobileGuideHint.title}
              description={fixedBottomMobileGuideHint.description}
              primaryAction={fixedBottomMobileGuideHint.primaryAction}
              secondaryAction={fixedBottomMobileGuideHint.secondaryAction}
              onSkip={fixedBottomMobileGuideHint.onSkip}
              onDone={fixedBottomMobileGuideHint.onDone}
              onDepositCrypto={fixedBottomMobileGuideHint.onDepositCrypto}
            />
          </div>
        </div>
      )}
    </CryptoModal>
  );
};

export default BuySellModal;
